/**
 * @fileoverview Drawer wrapper around the legacy FinalAssessment `StudentModalContent`.
 * Loads a single student's final assessment payload for the given project and renders
 * the legacy form inside `LumiarDrawer`. Saves are forwarded back through `onSaved`.
 */

import { useCallback, useEffect, useState } from 'react'
import { Box, Center, Loader } from '@mantine/core'
import { isEqual } from 'lodash'
import { useTranslation } from 'react-i18next'
import { Beforeunload } from 'react-beforeunload'
import LumiarDrawer from 'components/LumiarDrawer/LumiarDrawer'
import { Modal, IKind } from 'components/ModalLegacy'
import StudentModalContent from '@legacy/pages/FinalAssessment/components/StudentModalContent/StudentModalContent'
import finalAssessmentService from '@legacy/pages/FinalAssessment/components/StudentsAssessments/assessmentService'
import type {
  IFinalAssessmentScore,
  ServerData,
} from '@legacy/pages/FinalAssessment/service/interfaces'

/** Width of the legacy `StudentModalContent` (`@legacy/.../StudentModalContentStyled.ts`). */
const DRAWER_WIDTH = 700
const NOOP = () => {}

const EMPTY_PAYLOAD: ServerData = {
  moods: {
    mood_planning_worked: null,
    mood_group_got_involved: null,
    mood_group_took_part: null,
    mood_group_developed: null,
  },
  project_journal: '',
  project_id: '',
  final_assessment_scores: [],
  journal: { id: '', value: '' },
}

interface TaskTrackerFinalAssessmentDrawerProps {
  opened: boolean
  projectId: string
  studentId: string
  onClose: () => void
  onSaved?: () => void
}

/** Maps a legacy assessment score to the payload shape expected by the save endpoint. */
function toScorePayload(score: { id: string; doNotAssess: boolean; masterScore: number }): IFinalAssessmentScore {
  return {
    id: score.id,
    do_not_assess: score.doNotAssess,
    score: score.masterScore,
  }
}

export function TaskTrackerFinalAssessmentDrawer({
  opened,
  projectId,
  studentId,
  onClose,
  onSaved,
}: TaskTrackerFinalAssessmentDrawerProps) {
  const { t } = useTranslation()

  const [loading, setLoading] = useState(true)
  const [selectedStudent, setSelectedStudent] = useState<any | null>(null)
  const [payload, setPayload] = useState<ServerData>(EMPTY_PAYLOAD)
  const [initialPayload, setInitialPayload] = useState<ServerData>(EMPTY_PAYLOAD)

  useEffect(() => {
    if (!opened || !projectId || !studentId) {
      setLoading(false)
      return
    }
    let cancelled = false
    setLoading(true)

    const loadStudent = async () => {
      try {
        const student: any = await finalAssessmentService.getStudent(projectId, studentId)
        if (cancelled) return
        const contentScores: IFinalAssessmentScore[] = (student.contentFinalAssessmentScores ?? []).map(toScorePayload)
        const skillScores: IFinalAssessmentScore[] = (student.specificSkillFinalAssessmentScores ?? []).map(toScorePayload)
        const next: ServerData = {
          ...EMPTY_PAYLOAD,
          project_id: projectId,
          final_assessment_scores: [...contentScores, ...skillScores],
          journal: { id: student.masterJournalId, value: student.masterJournal ?? '' },
        }
        setSelectedStudent(student)
        setPayload(next)
        setInitialPayload(next)
      } catch {
        if (!cancelled) setSelectedStudent(null)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }

    loadStudent()
    return () => {
      cancelled = true
    }
  }, [opened, projectId, studentId])

  useEffect(() => {
    if (!opened) {
      setSelectedStudent(null)
      setPayload(EMPTY_PAYLOAD)
      setInitialPayload(EMPTY_PAYLOAD)
    }
  }, [opened])

  const hasChanges = !isEqual(payload, initialPayload)

  /** Asks for confirmation when there are unsaved changes, then runs `onConfirmed`. */
  const confirmCloseIfDirty = useCallback(
    (onConfirmed: () => void) => {
      if (!hasChanges) {
        onConfirmed()
        return
      }
      Modal({
        title: t('students_assessment_title_modal'),
        text: t('students_assessment_text_modal'),
        kind: IKind.ConfirmationExit,
      }).then(({ isConfirmed }) => {
        if (isConfirmed) onConfirmed()
      })
    },
    [hasChanges, t],
  )

  const handleDrawerChange = useCallback(
    (value: boolean) => {
      if (value) return
      confirmCloseIfDirty(onClose)
    },
    [confirmCloseIfDirty, onClose],
  )

  /** Legacy close handler. `didSave` is true only after the form's `onSave` succeeded. */
  const handleCloseDrawer = useCallback(
    (value: boolean, didSave = false) => {
      if (value) return
      const close = () => {
        if (didSave) onSaved?.()
        onClose()
      }
      if (didSave) close()
      else confirmCloseIfDirty(close)
    },
    [confirmCloseIfDirty, onClose, onSaved],
  )

  return (
    <>
      {hasChanges && opened && (
        <Beforeunload onBeforeunload={(event) => event.preventDefault()} />
      )}

      <LumiarDrawer
        visibility={opened}
        onChange={handleDrawerChange}
        side="right"
        scrollable={false}
      >
        <Box
          style={{
            width: DRAWER_WIDTH,
            height: '100vh',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
          }}
        >
          <Box style={{ flex: 1, minHeight: 0, display: 'flex' }}>
            {loading ? (
              <Center style={{ width: '100%' }}>
                <Loader size="md" />
              </Center>
            ) : selectedStudent ? (
              <StudentModalContent
                assessment_id={selectedStudent.finalAssessmentId}
                fullName={selectedStudent.fullName}
                imageUrl={selectedStudent.imageUrl || ''}
                contentsAssessments={selectedStudent.contentFinalAssessmentScores}
                skills={selectedStudent.specificSkillFinalAssessmentScores}
                masterjustification={selectedStudent.masterJournal}
                journalId={selectedStudent.masterJournalId}
                selectedStudent={selectedStudent}
                goNext={false}
                handleCloseDrawer={handleCloseDrawer}
                mixPanelTracker={NOOP}
                fetchStudents={() => onSaved?.()}
                saveAndGoNext={NOOP}
                journalStudent={selectedStudent.studentJournal}
                setPayload={setPayload}
                setInitialPayload={setInitialPayload}
                payload={payload}
                initialPayload={initialPayload}
                projectId={projectId}
                updateProject={async () => {}}
              />
            ) : (
              <Box p="md" style={{ width: '100%', textAlign: 'center', paddingTop: 40 }}>
                {t('task_tracker.drawer.error_loading')}
              </Box>
            )}
          </Box>
        </Box>
      </LumiarDrawer>
    </>
  )
}
